import { useEffect, useRef, useState } from 'react';
import { Download, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { MapBounds } from '../../types';
import { ensureHydrated, pickOfflineSuggestion } from '../../services/regionUsageService';
import { cellKeyToBounds } from '../../utils/grid';

interface OfflineSuggestNudgeProps {
  /** Master-flagg (feature + betalende bruker). */
  enabled: boolean;
  /** Åpne offline-nedlasting for området (Map.tsx flyr dit + åpner OfflineMapManager). */
  onAccept: (bounds: MapBounds) => void;
  /** Forsinkelse etter mount før første sjekk. */
  delayMs?: number;
}

const LS_KEY = 'fricamp_offline_suggested';

const readSuggested = (): Set<string> => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
};
const markSuggested = (key: string): void => {
  try {
    const set = readSuggested();
    set.add(key);
    localStorage.setItem(LS_KEY, JSON.stringify([...set]));
  } catch {
    /* localStorage utilgjengelig — ignore */
  }
};

/**
 * Foreslår offline-nedlasting av et område brukeren bruker MYE (regionUsageService).
 * Vises én gang per område (localStorage-huket) og aldri igjen etter aksept/avvis.
 * Betaler-gated fra Map (offline er premium). Rendres inne i Map, ingen App.tsx.
 */
const OfflineSuggestNudge: React.FC<OfflineSuggestNudgeProps> = ({ enabled, onAccept, delayMs = 12000 }) => {
  const { t } = useTranslation();
  const [cell, setCell] = useState<{ key: string; bounds: MapBounds } | null>(null);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let interval: ReturnType<typeof setInterval> | null = null;

    const check = async () => {
      if (checkedRef.current) return; // vis kun ett forslag om gangen
      await ensureHydrated();
      const suggested = readSuggested();
      const pick = pickOfflineSuggestion((k) => suggested.has(k));
      if (!pick) return;
      const bounds = cellKeyToBounds(pick.key);
      if (!bounds) return;
      checkedRef.current = true;
      setCell({ key: pick.key, bounds });
    };

    timer = setTimeout(() => {
      void check();
      // Sjekk igjen periodisk — brukeren kan bygge opp besøk i løpet av økta.
      interval = setInterval(() => { void check(); }, 60_000);
    }, delayMs);

    return () => {
      if (timer) clearTimeout(timer);
      if (interval) clearInterval(interval);
    };
  }, [enabled]);

  if (!cell) return null;

  const dismiss = () => { markSuggested(cell.key); setCell(null); };
  const accept = () => { markSuggested(cell.key); onAccept(cell.bounds); setCell(null); };

  return (
    <div className="fixed bottom-[calc(env(safe-area-inset-bottom,0px)+84px)] left-1/2 -translate-x-1/2 z-[3200] w-[340px] max-w-[92vw] animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="flex items-center gap-3 bg-[var(--bg-surface)]/97 backdrop-blur-md border border-emerald-500/40 rounded-2xl shadow-2xl px-3 py-2.5">
        <div className="w-9 h-9 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
          <Download size={18} className="text-emerald-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-tiny font-black text-[var(--text-primary)] leading-tight">
            {t('offline.suggest.title', 'Du bruker dette området ofte')}
          </div>
          <div className="text-3xs text-[var(--text-muted)] mt-0.5">
            {t('offline.suggest.body', 'Last det ned så det virker uten dekning.')}
          </div>
        </div>
        <button
          onClick={accept}
          className="px-3 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-2xs font-black uppercase tracking-widest transition-all active:scale-95 shrink-0"
        >
          {t('offline.suggest.download', 'Last ned')}
        </button>
        <button
          onClick={dismiss}
          aria-label={t('common.dismiss', 'Lukk')}
          className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)] transition-colors shrink-0"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default OfflineSuggestNudge;
